import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast, useToast } from '../../components/base/Toast';
import FormulaireClient from './components/FormulaireClient';
import FormulaireMesures from './components/FormulaireMesures';
import StepTypeCouture from './components/StepTypeCouture';
import StepTissuAssignation from './components/StepTissuAssignation';
import StepModeleSelection from './components/StepModeleSelection';
import FormulairePaiement from './components/FormulairePaiement';
import { api } from '../../services/api';
import FactureModal from '../../components/feature/FactureModal';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; }

const ROUTINE_STEPS = [
  { id: 1, label: 'Client', icon: 'ri-user-line', key: 'client' },
  { id: 2, label: 'Mesures', icon: 'ri-pencil-ruler-line', key: 'mesures' },
  { id: 3, label: 'Tissus', icon: 'ri-layout-grid-line', key: 'tissus' },
  { id: 4, label: 'Couture', icon: 'ri-t-shirt-line', key: 'couture' },
  { id: 5, label: 'Modèles', icon: 'ri-brush-line', key: 'modeles' },
  { id: 6, label: 'Paiement', icon: 'ri-money-franc-circle-line', key: 'paiement' },
];

const TECHNICAL_STEPS = [
  { id: 1, label: 'Client', icon: 'ri-user-line', key: 'client' },
  { id: 2, label: 'Mesures', icon: 'ri-pencil-ruler-line', key: 'mesures' },
  { id: 3, label: 'Couture', icon: 'ri-t-shirt-line', key: 'couture' },
  { id: 4, label: 'Tissus', icon: 'ri-layout-grid-line', key: 'tissus' },
  { id: 5, label: 'Modèles', icon: 'ri-brush-line', key: 'modeles' },
  { id: 6, label: 'Paiement', icon: 'ri-money-franc-circle-line', key: 'paiement' },
];

export default function CommandeNouvellePage() {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [user, setUser] = useState<UserInfo | null>(null);
  const { branding } = useBranding();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFacture, setShowFacture] = useState(false);
  const [factureData, setFactureData] = useState<any>(null);

  // Form state
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [clientId, setClientId] = useState('');
  const [mesures, setMesures] = useState<{ [key: string]: string; notes: string }>({ notes: '' });
  
  // Articles state (Multi-items)
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [stock, setStock] = useState<any[]>([]);
  const [orderFabrics, setOrderFabrics] = useState<any[]>([]);
  const [isTissusBeforeCouture, setIsTissusBeforeCouture] = useState(true);
  const [steps, setSteps] = useState<any[]>(ROUTINE_STEPS);

  const [paiement, setPaiement] = useState({ total: 0, remise: 0, acompte: 0, modePaiement: 'Espèces' });
  const [dateLivraison, setDateLivraison] = useState('');
  const [dateCommande, setDateCommande] = useState(''); // New state for custom order date

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/login');

    const savedOrder = localStorage.getItem('atelier_bassoum_ordre_etapes');
    if (savedOrder === 'technique') {
      setIsTissusBeforeCouture(false);
      setSteps(TECHNICAL_STEPS);
    } else {
      setIsTissusBeforeCouture(true);
      setSteps(ROUTINE_STEPS);
    }

    // Restore draft
    const draft = localStorage.getItem('atelier_bassoum_commande_draft');
    if (draft) {
      try {
        const d = JSON.parse(draft);
        if (d.nom) setNom(d.nom);
        if (d.telephone) setTelephone(d.telephone);
        if (d.clientId) setClientId(d.clientId);
        if (d.mesures) setMesures(d.mesures);
        if (d.selectedItems) setSelectedItems(d.selectedItems);
        if (d.orderFabrics) setOrderFabrics(d.orderFabrics);
        if (d.paiement) setPaiement(d.paiement);
        if (d.step) setStep(d.step);
      } catch (e) { console.error("Error restoring draft", e); }
    }

    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (token) {
        try {
          // fetch stock if needed
          const stockData = await api.getStocks(token);
          if (stockData) setStock(stockData);
        } catch (e) { console.error("Error fetching initial data", e); }
      }
    };
    fetchData();

    const d = new Date();
    // Default delivery in 7 days
    const deliv = new Date();
    deliv.setDate(deliv.getDate() + 7);
    setDateLivraison(deliv.toISOString().split('T')[0]);
    
    // Default order date is now (for datetime-local)
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setDateCommande(now.toISOString().slice(0, 16));
  }, [navigate]);

  // Save draft
  useEffect(() => {
    const draft = {
      nom, telephone, clientId, mesures, selectedItems, orderFabrics, paiement, step
    };
    localStorage.setItem('atelier_bassoum_commande_draft', JSON.stringify(draft));
  }, [nom, telephone, clientId, mesures, selectedItems, orderFabrics, paiement, step]);

  // Recalculer le total quand les articles ou le stock changent
  useEffect(() => {
    let totalCouture = 0;
    let totalTissus = 0;

    selectedItems.forEach(item => {
      totalCouture += (Number(item.prix_unitaire) || 0) * (Number(item.quantite) || 0);
      
      // Ajouter le prix des tissus boutique
      if (item.tissus && Array.isArray(item.tissus)) {
        item.tissus.forEach((t: any) => {
          if (!t.is_client_cloth && t.tissu_stock_id) {
            const s = stock.find(st => String(st.id) === String(t.tissu_stock_id));
            if (s) {
              totalTissus += (Number(s.prix_unitaire) || 0) * (Number(t.quantite) || 0);
            }
          }
        });
      }
    });

    const grandTotal = totalCouture + totalTissus;
    setPaiement(prev => ({ ...prev, total: grandTotal }));
  }, [selectedItems, stock]);

  const handleMesureChange = (field: string, value: string) => {
    setMesures(prev => ({ ...prev, [field]: value }));
  };

  const handlePaiementChange = (field: keyof typeof paiement, value: string | number) => {
    setPaiement(prev => ({ ...prev, [field]: value }));
  };

  const canNext = () => {
    const currentKey = steps[step - 1]?.key;
    if (currentKey === 'client') return nom.trim() !== '' && telephone.trim() !== '';
    if (currentKey === 'couture') return selectedItems.length > 0;
    return true;
  };

  const handleSubmit = async () => {
    if (!nom || !telephone) { showToast('Veuillez renseigner le client.', 'error'); return; }
    if (selectedItems.length === 0) { showToast('Sélectionnez au moins un article.', 'error'); return; }
    
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) { navigate('/login'); return; }

    setLoading(true);
    try {
      let finalClientId = clientId;

      // 1. Client management
      if (!finalClientId) {
        const resClient = await api.createClient(token, { nom, telephone });
        finalClientId = String(resClient.id);
      }

      // 2. Mesures management
      const mesuresArray = Object.entries(mesures)
        .filter(([k]) => k !== 'notes')
        .map(([k, v]) => ({ champ_mesure_id: Number(k), valeur: v }));
      
      if (mesuresArray.length > 0) {
        await api.saveClientMesures(token, Number(finalClientId), mesuresArray);
      }

      // 3. Articles & Custom Photos management
      const finalArticles = [];
      for (const item of selectedItems) {
          let mid = item.modele_id;
          
          // Si photo custom prise, on l'upload et on crée un modèle temporaire
          if (item.modele_custom_file) {
              const uploadRes = await api.uploadImage(token, item.modele_custom_file);
              const newModel = await api.createModele(token, {
                  nom: `Photo Commande ${item.nom}`,
                  image_url: uploadRes.image_url,
                  categorie_id: 1 // Catégorie par défaut
              });
              mid = newModel.id;
          }

          const tissusAvecPrix = (item.tissus || []).map((t: any) => {
              // Si c'est un tissu de la boutique et qu'il y a un ID de stock
              if (!t.is_client_cloth && t.tissu_stock_id) {
                  // Recherche approfondie dans le stock chargé
                  const s = stock.find(st => String(st.id) === String(t.tissu_stock_id));
                  if (s) {
                      const pu = Number(s.prix_unitaire) || 0;
                      const qte = Number(t.quantite) || 0;
                      return {
                          ...t,
                          prix_unitaire: pu,
                          total_ligne: pu * qte,
                          tissu_nom: s.nom, // Ajouté pour affichage immédiat
                          tissu_unite: s.unite // Ajouté pour affichage immédiat
                      };
                  }
              }
              // Tissu client ou non trouvé : prix à 0
              return { 
                ...t, 
                prix_unitaire: Number(t.prix_unitaire) || 0, 
                total_ligne: Number(t.total_ligne) || 0,
                tissu_nom: t.description || (t.is_client_cloth ? 'Tissu client' : 'Tissu inconnu')
              };
          });

          finalArticles.push({
              type_habit_id: item.type_habit_id,
              type_habit_nom: item.nom, // Ajouté pour affichage immédiat
              modele_id: mid,
              modele_nom: item.modele_nom || (item.modele_custom_file ? 'Photo personnalisée' : 'Sur mesure'),
              quantite: item.quantite,
              prix_unitaire: item.prix_unitaire,
              total_ligne: item.prix_unitaire * item.quantite,
              tissus: tissusAvecPrix
          });
      }

      // 4. Créer la commande
      const commandeData = {
        client_id: Number(finalClientId),
        statut: 'EN_ATTENTE',
        vendeur_id: user?.id,
        total: paiement.total,
        remise: paiement.remise,
        acompte: paiement.acompte,
        reste: Math.max(0, (paiement.total - paiement.remise) - paiement.acompte),
        mode_paiement: paiement.modePaiement,
        date_livraison: dateLivraison,
        dateLivraison: dateLivraison, // Doublon camelCase pour compatibilité FactureModal
        notes: mesures.notes,
        mesures: mesuresArray,
        articles: finalArticles,
        date_commande: dateCommande.replace('T', ' ') + ':00'
      };

      const result = await api.createCommande(token, commandeData);
      showToast(`Commande ${result.code} créée !`, 'success');
      localStorage.removeItem('atelier_bassoum_commande_draft'); // Clean draft
      
      // 5. Facture immédiate
      const fullCommandeRecap = {
        ...commandeData,
        id: result.id,
        code: result.code,
        vendeur_nom: user?.nom || 'Vendeur',
        client: { id: finalClientId, nom, telephone },
        paiement: {
          total: paiement.total,
          remise: paiement.remise,
          acompte: paiement.acompte,
          reste: Math.max(0, (paiement.total - paiement.remise) - paiement.acompte),
          modePaiement: paiement.modePaiement,
          vendeur: user?.nom || 'Vendeur'
        }
      };

      setFactureData({ 
        type: 'commande', 
        data: {
          ...fullCommandeRecap,
          modele: finalArticles.length === 1 ? finalArticles[0].modele_nom : `${finalArticles.length} articles`,
          articles: finalArticles.map(art => ({
            ...art,
            tissus: art.tissus.map(t => ({
              ...t,
              tissu_nom: t.tissu_nom // Déjà enrichi plus haut
            }))
          }))
        } 
      });
      setShowFacture(true);
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de l\'enregistrement.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={() => navigate(-1)} title="Retour" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
            <i className="ri-arrow-left-line" />
          </button>
          <h2 className="font-playfair font-bold text-xl text-gray-900">Nouvelle commande</h2>
          <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Étape {step}/6</span>
              <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-foret-500 transition-all duration-500 ${
                      step === 1 ? 'w-1/6' : 
                      step === 2 ? 'w-2/6' : 
                      step === 3 ? 'w-3/6' : 
                      step === 4 ? 'w-4/6' : 
                      step === 5 ? 'w-5/6' : 
                      'w-full'
                    }`}
                    role="progressbar"
                    aria-valuenow={step}
                    aria-valuemin={1}
                    aria-valuemax={6}
                    aria-label={`Étape ${step} sur 6`}
                  />
              </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-4 lg:p-6 pb-24">
             {/* Progress steps */}
             <div className="flex justify-between mb-8 overflow-x-auto pb-4 px-1 gap-4 scrollbar-hide">
                {steps.map(s => (
                  <div key={s.id} className="flex flex-col items-center gap-2 min-w-[60px]">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        step === s.id ? 'bg-foret-500 text-white border-foret-500 shadow-md scale-110' : 
                        step > s.id ? 'bg-foret-100 text-foret-600 border-foret-500' : 'bg-gray-100 text-gray-400 border-gray-200'
                    }`}>
                      {step > s.id ? <i className="ri-check-line font-bold" /> : <i className={s.icon} />}
                    </div>
                    <span className={`text-[9px] font-bold uppercase transition-colors ${step === s.id ? 'text-foret-600' : 'text-gray-400'}`}>{s.label}</span>
                  </div>
                ))}
             </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 overflow-hidden">
              <div className="p-6 md:p-8">
                {steps[step - 1]?.key === 'client' && <FormulaireClient nom={nom} telephone={telephone} onNomChange={setNom} onTelChange={setTelephone} onClientSelect={setClientId} />}
                {steps[step - 1]?.key === 'mesures' && <FormulaireMesures mesures={mesures} onChange={handleMesureChange} clientId={clientId} />}
                {steps[step - 1]?.key === 'tissus' && <StepTissuAssignation selectedItems={selectedItems} onChange={setSelectedItems} stock={stock} isGlobal={isTissusBeforeCouture} orderFabrics={orderFabrics} onOrderFabricsChange={setOrderFabrics} />}
                {steps[step - 1]?.key === 'couture' && <StepTypeCouture selectedItems={selectedItems} onChange={setSelectedItems} isTissusBeforeCouture={isTissusBeforeCouture} orderFabrics={orderFabrics} />}
                {steps[step - 1]?.key === 'modeles' && <StepModeleSelection selectedItems={selectedItems} onChange={setSelectedItems} />}
                {steps[step - 1]?.key === 'paiement' && (
                  <div className="space-y-6">
                    <FormulairePaiement paiement={paiement} onChange={handlePaiementChange} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                        <label htmlFor="commande-date-creation" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-widest">Date de la commande</label>
                        <div className="relative">
                            <i className="ri-calendar-line absolute left-4 top-1/2 -translate-y-1/2 text-foret-500" />
                            <input id="commande-date-creation" type="datetime-local" value={dateCommande} onChange={e => setDateCommande(e.target.value)}
                              className="w-full pl-11 pr-4 py-3 text-sm border-none rounded-xl focus:ring-2 focus:ring-foret-500 bg-white font-bold shadow-sm" />
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                        <label htmlFor="commande-date-livraison" className="block text-[10px] font-bold text-gray-400 mb-2 uppercase tracking-widest">Date de livraison souhaitée</label>
                        <div className="relative">
                            <i className="ri-calendar-event-line absolute left-4 top-1/2 -translate-y-1/2 text-foret-500" />
                            <input id="commande-date-livraison" type="date" value={dateLivraison} onChange={e => setDateLivraison(e.target.value)}
                              className="w-full pl-11 pr-4 py-3 text-sm border-none rounded-xl focus:ring-2 focus:ring-foret-500 bg-white font-bold shadow-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="fixed bottom-6 left-0 right-0 max-w-3xl mx-auto px-4 pointer-events-none">
                <div className="flex justify-between items-center bg-white/80 backdrop-blur-md p-2 rounded-2xl border border-white shadow-2xl pointer-events-auto">
                    <button
                        onClick={() => step > 1 ? setStep(s => s - 1) : navigate(-1)}
                        className="px-6 py-3 rounded-xl text-gray-600 font-bold text-sm hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                        {step === 1 ? 'Annuler' : 'Retour'}
                    </button>

                    <button
                        onClick={() => step < steps.length ? setStep(s => s + 1) : handleSubmit()}
                        disabled={loading || !canNext()}
                        className={`px-8 py-3 rounded-xl font-bold text-sm shadow-xl transition-all cursor-pointer flex items-center gap-2 ${
                        loading ? 'bg-gray-100 text-gray-400' : 'bg-foret-500 hover:bg-foret-600 text-white hover:-translate-y-0.5'
                        }`}
                    >
                        {loading ? 'Finalisation...' : step === steps.length ? 'Confirmer la commande' : 'Continuer'}
                        {!loading && step < steps.length && <i className="ri-arrow-right-line" />}
                    </button>
                </div>
            </div>
          </div>
        </div>
      </div>

      {showFacture && factureData && (
        <FactureModal 
          entreprise={branding} 
          factureData={factureData} 
          onClose={() => {
            setShowFacture(false);
            navigate('/dashboard');
          }} 
        />
      )}
    </div>
  );
}
